import {
  getFormattedDateTime,
  DispatchRequestEntity,
  DispatchRequestTrackingResult,
  DispatchTrackingPerformer
} from "@ots-frontend/ots-data";
import {StatusTone} from "../../components/StatusPill";

type StatusLike = string | {code?: string} | undefined;

const terminalStatuses = new Set([
  "CANCELED",
  "CANCELLED",
  "CLOSED",
  "COMPLETE",
  "COMPLETED",
  "PENDING_CANCEL",
  "REASSIGNED",
  "REJECTED",
  "UNRESOLVED"
]);

export const getStatusCode = (status: StatusLike): string =>
  (typeof status === "string" ? status : status?.code)?.toUpperCase() ?? "UNKNOWN";

export const getDispatchRequestStatusLabel = (request: DispatchRequestEntity): string => {
  const displayValue = request.status.displayValue;
  return typeof displayValue === "string" ? displayValue : displayValue.defaultMessage;
};

export const getDispatchRequestStatusTone = (request: DispatchRequestEntity): StatusTone => {
  switch (getStatusCode(request.status)) {
    case "COMPLETED":
    case "COMPLETE":
      return "success";
    case "REJECTED":
    case "CANCELED":
    case "CANCELLED":
    case "PENDING_CANCEL":
    case "UNRESOLVED":
      return "danger";
    case "PENDING":
      return "warning";
    case "ACCEPTED":
    case "STARTED":
    case "PICKED_UP":
      return "info";
    default:
      return "neutral";
  }
};

export const getDispatchRequestStageIndex = (request: DispatchRequestEntity): number => {
  switch (getStatusCode(request.status)) {
    case "COMPLETED":
    case "COMPLETE":
      return 4;
    case "STARTED":
    case "PICKED_UP":
      return 3;
    case "ACCEPTED":
      return 2;
    default:
      return request.isAuthorized ? 1 : 0;
  }
};

export const isDispatchTrackingClosedStatus = (status: StatusLike): boolean =>
  terminalStatuses.has(getStatusCode(status));

/** The tracking fields that decide how often to poll; dispatch and dispatch request results both fit. */
type PollableTracking = Pick<DispatchRequestTrackingResult, "confidence" | "performer" | "route"> & {status?: StatusLike};

export const getDispatchTrackingPollingInterval = (
  entityStatus: StatusLike,
  tracking?: PollableTracking
): number => {
  const status = getStatusCode(tracking?.status ?? entityStatus);

  if (terminalStatuses.has(status)) {
    return 0;
  }

  if (tracking?.confidence?.trackable === false && !tracking.performer) {
    return 60000;
  }

  if (tracking?.performer?.locationContext?.location || status === "STARTED" || status === "PICKED_UP") {
    return tracking?.route?.etaDttm ? 15000 : 30000;
  }

  return status === "PENDING" || status === "ACCEPTED" ? 60000 : 30000;
};

export const formatOtsDateTime = (value?: number): string =>
  value == null ? "—" : getFormattedDateTime(value);

export const formatTrackingDuration = (durationMillis?: number): string | undefined =>
  typeof durationMillis === "number" ? `${Math.round(durationMillis / 60000)} min` : undefined;

export const getTrackingLocationDttm = (performer?: DispatchTrackingPerformer): number | undefined =>
  performer?.locationDttm ?? performer?.locationContext?.location?.dttm;

export const getTrackingOriginLabel = (performer?: DispatchTrackingPerformer): string =>
  performer?.locationContext?.meta?.origin?.siteName ??
  performer?.locationContext?.meta?.origin?.source ??
  "Unknown";
